import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useParameters from "./hooks/useParameters";
import { ToggleChip } from "../../components/Chip";
import Label from "../../components/Label";
import Button from "../../components/Button";
import styles from "./parameters.module.css";
import WidthValueModelModal from "./WidthValueModelModal";
import ColorValueModal from "./ColorValueModal";
import ColorMappingModal from "./ColorMappingModal";
import {
  addColorSuffix,
  selectColorMappings,
  selectWizardParameters,
  updateWizardParameter,
} from "./configurationsSlice";
import WizardEditorModal from "./WizardEditorModal";
import NumberSeqEditorModal from "./NumberSeqEditorModal";

const WIDTH_ID = "WIDTH";
const COLOR_ID = "COLOR";

const uniq = (arr) => Array.from(new Set(arr));

const Parameters = ({
  parameters: parametersFromProps,
  disableChange,
  onDeleteParamValue = () => {},
  widthExtraValues = [],
  setWidthExtraValues = () => {},
  widthModelsByValue = {},
  setWidthModelsByValue = () => {},
  colorExtraValues = [],
  setColorExtraValues = () => {},
  onValueAdded = () => {},
}) => {
  const dispatch = useDispatch();
  const { configurationId } = useParams();
  const colorMappings = useSelector(selectColorMappings(configurationId));
  const wizardParameters = useSelector(selectWizardParameters(configurationId));
  const { parameters, handleClick } = useParameters({
    parameters: parametersFromProps,
  });

  const [isWidthModalOpen, setIsWidthModalOpen] = useState(false);
  const [isWidthEditModalOpen, setIsWidthEditModalOpen] = useState(false);
  const [editingWidthValue, setEditingWidthValue] = useState(null);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [colorMappingValue, setColorMappingValue] = useState(null);
  const [editingWizard, setEditingWizard] = useState(null);
  const [editingNumSeq, setEditingNumSeq] = useState(null);

  const mergedParameters = useMemo(() => {
    return parameters.map((p) => {
      if (p.id === WIDTH_ID) {
        return { ...p, values: uniq([...(p.values || []), ...widthExtraValues]) };
      }
      if (p.id === COLOR_ID) {
        return { ...p, values: uniq([...(p.values || []), ...colorExtraValues]) };
      }
      return p;
    });
  }, [parameters, widthExtraValues, colorExtraValues]);

  const handleDelete = (paramId, paramValue) => {
    // let parent delete if it exists there
    onDeleteParamValue(paramId, paramValue);

    // also delete local-only width value/model if applicable
    if (paramId === WIDTH_ID) {
      setWidthExtraValues((prev) => prev.filter((v) => v !== paramValue));

      setWidthModelsByValue((prev) => {
        const existing = prev?.[paramValue];
        if (existing?.previewUrl) {
          try {
            URL.revokeObjectURL(existing.previewUrl);
          } catch (_) {}
        }
        const next = { ...prev };
        delete next[paramValue];
        return next;
      });
    }

    if (paramId === COLOR_ID) {
      setColorExtraValues((prev) => prev.filter((v) => v !== paramValue));
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gap)",
      }}
    >
      {mergedParameters.map((parameter) => (
        <div
          key={parameter.id}
          style={{ display: "flex", alignItems: "center" }}
        >
          <div style={{ width: "var(--width-32)", flexShrink: "0" }}>
            <Label>{parameter.label}</Label>
          </div>

          <div
            style={{
              display: "flex",
              gap: "var(--gap-sm)",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {parameter.values.map((parameterValue) => {
              return (
                <div key={parameterValue} className={styles.chipContainer}>
                  <ToggleChip
                    active={parameterValue === parameter.selectedValue}
                    onToggle={() => {
                      handleClick({ id: parameter.id, value: parameterValue });
                    }}
                  >
                    {typeof parameterValue === "number"
                      ? `${parameterValue * 10} mm`
                      : parameterValue}
                  </ToggleChip>

                  <div className={styles.actionButtons}>
                    {parameter.id === COLOR_ID && (
                      <Button
                        inline
                        icon={"pencil"}
                        onClick={() => setColorMappingValue(parameterValue)}
                        disabled={disableChange}
                      />
                    )}
                    <Button
                      inline
                      icon={"trashIcon"}
                      onClick={() => handleDelete(parameter.id, parameterValue)}
                      disabled={disableChange}
                    />
                  </div>
                </div>
              );
            })}

            {/* Plus button behavior */}
            {parameter.id === WIDTH_ID ? (
              <>
                <Button
                  inline
                  icon={"plus"}
                  disabled={disableChange}
                  onClick={() => setIsWidthModalOpen(true)}
                />
                <WidthValueModelModal
                  isOpen={isWidthModalOpen}
                  onClose={() => setIsWidthModalOpen(false)}
                  colorValues={
                    (mergedParameters.find((p) => p.id === COLOR_ID)?.values || [])
                      .filter((v) => colorMappings[v]?.length > 0)
                  }
                  colorElementMappings={colorMappings}
                  onSave={({
                    storedValue,
                    cmValue,
                    mmValue,
                    file,
                    previewUrl,
                    componentMapping,
                    selectedColors,
                  }) => {
                    setWidthExtraValues((prev) => uniq([...prev, storedValue]));

                    setWidthModelsByValue((prev) => ({
                      ...prev,
                      [storedValue]: { file, previewUrl, cmValue, mmValue, componentMapping, selectedColors },
                    }));
                    onValueAdded(WIDTH_ID, storedValue);
                  }}
                />
              </>
            ) : parameter.id === COLOR_ID ? (
              <>
                <Button
                  inline
                  icon={"plus"}
                  disabled={disableChange}
                  onClick={() => setIsColorModalOpen(true)}
                />
                <ColorValueModal
                  isOpen={isColorModalOpen}
                  onClose={() => setIsColorModalOpen(false)}
                  onSave={({ suffix, label }) => {
                    setColorExtraValues((prev) => uniq([...prev, label]));
                    dispatch(
                      addColorSuffix({
                        configurationId,
                        colorLabel: label,
                        suffix,
                      }),
                    );
                    onValueAdded(COLOR_ID, label);
                  }}
                />
              </>
            ) : (
              <Button inline icon={"plus"} disabled={disableChange} />
            )}
          </div>
        </div>
      ))}
      {wizardParameters.map((wizard) => {
        const isNumSeq = wizard.type === "NUMBER_SEQ";
        const valuesCount = wizard.steps?.[0]?.options?.length || 0;

        const getSummary = () => {
          if (isNumSeq) {
            return valuesCount === 0
              ? "Nėra reikšmių"
              : `${valuesCount} ${valuesCount === 1 ? "reikšmė" : "reikšmės"}`;
          }
          return wizard.steps.length === 0
            ? "Nėra žingsnių"
            : `${wizard.steps.length} ${wizard.steps.length === 1 ? "žingsnis" : "žingsniai"}`;
        };

        return (
          <div
            key={wizard.id}
            style={{ display: "flex", alignItems: "center" }}
          >
            <div style={{ width: "var(--width-32)", flexShrink: "0" }}>
              <Label>{wizard.label}</Label>
            </div>
            <div
              style={{
                display: "flex",
                gap: "var(--gap-sm)",
                alignItems: "center",
              }}
            >
              <span style={{ opacity: 0.6 }}>{getSummary()}</span>
              <Button
                inline
                icon="pencil"
                disabled={disableChange}
                onClick={() =>
                  isNumSeq ? setEditingNumSeq(wizard) : setEditingWizard(wizard)
                }
              />
            </div>
          </div>
        );
      })}
      <ColorMappingModal
        isOpen={colorMappingValue != null}
        onClose={() => setColorMappingValue(null)}
        colorLabel={colorMappingValue}
      />
      <WidthValueModelModal
        isOpen={isWidthEditModalOpen}
        onClose={() => {
          setIsWidthEditModalOpen(false);
          setEditingWidthValue(null);
        }}
        colorValues={
          (mergedParameters.find((p) => p.id === COLOR_ID)?.values || [])
            .filter((v) => colorMappings[v]?.length > 0)
        }
        colorElementMappings={colorMappings}
        editingValue={editingWidthValue}
        existingModel={editingWidthValue != null ? widthModelsByValue[editingWidthValue] : null}
        onSave={({ file, previewUrl, componentMapping, selectedColors }) => {
          setWidthModelsByValue((prev) => ({
            ...prev,
            [editingWidthValue]: {
              ...prev[editingWidthValue],
              file: file || prev[editingWidthValue]?.file,
              previewUrl: previewUrl || prev[editingWidthValue]?.previewUrl,
              componentMapping,
              selectedColors,
            },
          }));
          setIsWidthEditModalOpen(false);
          setEditingWidthValue(null);
        }}
      />
      <WizardEditorModal
        isOpen={editingWizard != null}
        onClose={() => setEditingWizard(null)}
        wizardParam={editingWizard}
        onSave={(steps) => {
          dispatch(
            updateWizardParameter({
              configurationId,
              wizardId: editingWizard.id,
              steps,
            }),
          );
        }}
      />
      <NumberSeqEditorModal
        isOpen={editingNumSeq != null}
        onClose={() => setEditingNumSeq(null)}
        param={editingNumSeq}
        onSave={(values) => {
          dispatch(
            updateWizardParameter({
              configurationId,
              wizardId: editingNumSeq.id,
              steps: [
                {
                  id: editingNumSeq.steps?.[0]?.id || `step_${Date.now()}`,
                  label: editingNumSeq.label,
                  options: values,
                },
              ],
            }),
          );
        }}
      />
    </div>
  );
};

export default Parameters;
